import React from "react";
import { InfoDot } from "../../components/classic/InfoDot";
import { inks, paper } from "./viz";
export { Pager } from "./viz";

/**
 * parts — the chrome the graphical dashboard repeats.
 *
 * Ported from the mock's `.card` / `.shead` rules. Kept here rather than reused
 * from `primitives.jsx` because the mock's panel has its own radius, two-layer
 * shadow and hover lift, and bending the shared SectionCard to match would
 * change every other screen that uses it.
 */

/** A panel. `click` makes the whole surface a door, with a real lift on hover. */
export function GPanel({ t, click, onClick, children, style, className, ...rest }) {
  const [hover, setHover] = React.useState(false);
  const Tag = click ? "button" : "div";
  return (
    <Tag
      {...rest}
      className={className}
      onClick={onClick}
      onMouseEnter={click ? () => setHover(true) : undefined}
      onMouseLeave={click ? () => setHover(false) : undefined}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: paper(t).card,
        border: `1px solid ${hover ? t.c.borderStrong : t.c.border}`,
        borderRadius: t.r.card,
        padding: "17px 18px",
        boxShadow: hover
          ? `0 1px 2px ${t.c.shadowColor}, 0 16px 34px -18px ${t.c.shadowColor}`
          : `0 1px 2px ${t.c.shadowColor}, 0 6px 18px -12px ${t.c.shadowColor}`,
        transform: hover ? "translateY(-3px)" : "none",
        transition: "box-shadow .2s cubic-bezier(.16,1,.3,1), border-color .2s, transform .2s cubic-bezier(.16,1,.3,1)",
        cursor: click ? "pointer" : "default",
        fontFamily: t.font.family,
        color: t.c.text,
        position: "relative",
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/**
 * The section eyebrow. Uppercase micro-label with a fading rule, so panel
 * titles stop competing with section titles — the fix from mock rev 4.
 */
export function SectionHead({ t, title, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 11, margin: "30px 2px 14px" }}>
      <h2 style={{
        margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 1.1,
        textTransform: "uppercase", color: t.c.textSecondary,
      }}>
        {title}
      </h2>
      {hint && <p style={{ margin: 0, fontSize: 12, color: t.c.textTertiary }}>{hint}</p>}
      <span style={{
        flex: 1, height: 1,
        background: `linear-gradient(to right, ${t.c.border}, transparent)`,
      }} />
    </div>
  );
}

/** The panel header: title, meta, and the ⓘ every graph carries. */
export function PanelHead({ t, title, subtitle, info, cue, icon, tone = "blue", kicker, chips }) {
  const chipList = (chips || []).filter(Boolean).map((c) => (typeof c === "string" ? { label: c } : c));
  const ink = inks(t)[tone] || inks(t).blue;
  const ppr = paper(t);
  const headBg = t.isDark
    ? `color-mix(in srgb, ${ink} 10%, ${ppr.well})`
    : `color-mix(in srgb, ${ink} 7%, ${ppr.well})`;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, margin: "-17px -18px 0", padding: "11px 16px 10px",
        background: headBg,
        borderBottom: `1px solid ${t.c.border}`,
        borderTopLeftRadius: t.r.card,
        borderTopRightRadius: t.r.card,
      }}>
        <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
          {icon ? (
            <span aria-hidden="true" style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: t.isDark ? `${ink}26` : `${ink}14`,
              color: ink,
            }}>{icon}</span>
          ) : null}
          <div style={{ minWidth: 0, flex: 1 }}>
            {kicker ? <div style={{ ...t.font.statLabel, color: ink, marginBottom: 2 }}>{kicker}</div> : null}
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
              <h3 style={{
                margin: 0, fontSize: kicker ? 16 : 14.5, fontWeight: 650,
                letterSpacing: "-0.02em", lineHeight: 1.25, color: t.c.text,
              }}>{title}</h3>
              {subtitle && (
                <span style={{
                  fontSize: 12, fontWeight: 550, color: t.c.textSecondary,
                  letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums",
                }}>{subtitle}</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {cue && <span style={{ fontSize: 11, color: t.c.textSecondary, whiteSpace: "nowrap" }}>{cue}</span>}
          {info && <InfoDot metric={info} tone="graphical" />}
        </div>
      </div>
      {chipList.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, paddingLeft: icon ? 38 : 0 }}>
          {chipList.map((c, i) => {
            const hue = c.tone || null;
            return (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
                fontSize: 12, fontWeight: 650, padding: "5px 11px", borderRadius: 999,
                background: hue ? `${hue}1f` : `${ink}12`,
                border: `1px solid ${hue ? `${hue}55` : `${ink}3a`}`,
                color: hue || t.c.text, fontVariantNumeric: "tabular-nums",
              }}>
                {i === 0 && !hue ? <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: ink, flexShrink: 0 }} /> : null}
                {c.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Delta chip with the direction arrow the mock added in rev 4. */
export function Chip({ t, dir = "flat", children }) {
  const map = {
    up: { bg: `${t.status.positive}22`, fg: t.status.positive, ar: "▲" },
    down: { bg: `${t.status.negative}22`, fg: t.status.negative, ar: "▼" },
    flat: { bg: t.c.surfaceMuted, fg: t.c.textTertiary, ar: "—" },
  };
  const s = map[dir] || map.flat;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600,
      padding: "3px 8px", borderRadius: 999, background: s.bg, color: s.fg,
    }}>
      <span style={{ fontSize: 9, lineHeight: 1 }}>{s.ar}</span>
      {children}
    </span>
  );
}
